<template>
  <SettingsPaneV2 header="API Keys">
    <template #subheader>Configure your integrations' scopes and settings</template>
    <template #header-action>
      <DarwinButton
        size="md"
        kind="primary"
        flair="rounded"
        @click="openCreateKeyDialogRef = true"
      >
        New API Key
      </DarwinButton>
    </template>

    <template #body>
      <AccessInstructions
        class="key-management__instructions"
        :snippets="snippets"
      />
      <div class="key-management">
        <Spinner
          v-if="loading"
          class="spinner"
          size="md"
          color="dark"
          label="keys loading"
        />
        <template v-else>
          <template v-if="teamStore.currentTeam && userKeys.length > 0">
            <div class="key-management__header">Existing keys</div>
            <div
              v-if="teamStore.currentTeam"
              class="key-management__description"
            >
              {{ `These are for all of your workspaces, not just ${teamStore.currentTeam.name}` }}
            </div>

            <div class="key-management__list">
              <KeyListItem
                v-for="apiKey in userKeys"
                :key="apiKey.id"
                :api-key="apiKey"
              >
                <template #icon>
                  <KeyTeamAvatar :api-key="apiKey" />
                </template>
              </KeyListItem>
            </div>
          </template>

          <template v-if="teamStore.currentTeam && teamKeys.length > 0">
            <div class="key-management__header">{{ teamStore.currentTeam.name }} keys</div>
            <div class="key-management__description">
              {{
                `You're seeing keys by other members
                because you're an administrator in ${teamStore.currentTeam.name}`
              }}
            </div>

            <div class="key-management__list">
              <KeyListItem
                v-for="apiKey in teamKeys"
                :key="apiKey.id"
                :api-key="apiKey"
              >
                <template #icon>
                  <KeyOwnerAvatar :api-key="apiKey" />
                </template>
              </KeyListItem>
            </div>
          </template>
        </template>
      </div>
    </template>

    <CreateKeyDialog
      v-if="openCreateKeyDialogRef && teamStore.currentTeam"
      v-model:open="openCreateKeyDialogRef"
      :team="teamStore.currentTeam"
      @cancel="openCreateKeyDialogRef = false"
    />
  </SettingsPaneV2>
</template>

<script lang="ts">
export default {
  name: 'ApiKeys',
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Spinner from '@/uiKit/Spinner/Spinner.vue'

import { useTeamStore } from '@/pinia/useTeamStore'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { useApiKeyLoader } from '@/modules/ApiKeys/useApiKeyLoader'
import { useApiKeyStore } from '@/modules/ApiKeys/useApiKeyStore'

import AccessInstructions from '@/modules/ApiKeys/AccessInstructions.vue'
import CreateKeyDialog from '@/modules/ApiKeys/CreateKeyDialog.vue'
import KeyListItem from '@/modules/ApiKeys/KeyListItem.vue'
import KeyOwnerAvatar from '@/modules/ApiKeys/KeyOwnerAvatar.vue'
import KeyTeamAvatar from '@/modules/ApiKeys/KeyTeamAvatar.vue'
import { ACCESS_CODES } from '@/modules/ApiKeys/snippets'
import { SupportedLanguages } from '@/modules/ApiKeys/types'
import type { AccessCodeExample } from '@/modules/ApiKeys/types'

import SettingsPaneV2 from './SettingsPaneV2.vue'

/**
 * Renders general API key management UI.
 *
 * Lists API keys accessible by user. These can be
 *
 * - keys created by user, in all of the user's teams
 * - keys created by other users, in the current team
 *
 * Allows creation and revoking of keys.
 *
 * Created keys are associated to current team.
 */

const snippets: AccessCodeExample[] = [
  { language: SupportedLanguages.cli, code: ACCESS_CODES[SupportedLanguages.cli] },
  { language: SupportedLanguages.python, code: ACCESS_CODES[SupportedLanguages.python] },
]

const openCreateKeyDialogRef = ref(false)

const keyStore = useApiKeyStore()

const userStore = useUserStore()
const userKeys = computed(() =>
  keyStore.apiKeys.filter((k) =>
    userStore.currentUserId ? k.user_id === userStore.currentUserId : [],
  ),
)

const teamStore = useTeamStore()

const teamKeys = computed(() =>
  keyStore.apiKeys.filter((k) =>
    teamStore.currentTeam ? k.team_id === teamStore.currentTeam.id : [],
  ),
)

const { loading, load: loadKeys } = useApiKeyLoader()

onMounted(() => loadKeys())
</script>

<style lang="scss" scoped>
.spinner {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
}

.key-management {
  position: relative;
  width: 100%;
}

.key-management__header {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-top: 4px;
}

.key-management__description {
  @include typographyRegularBody200;
  margin-bottom: 28px;
  color: var(--color-text-subtlest);
}
.key-management__list,
.key-management__instructions {
  margin-bottom: 24px;
}
</style>
